import React, { useState, useEffect } from 'react';
import { Calendar, Plus, X, Check, Pencil, Trash2 } from 'lucide-react';
import { Matchday, Season, Competition } from '../../types/sports';
import { sportsApi } from '../../services/sportsApi';

const MatchdaysDataTable: React.FC = () => {
  const [matchdays, setMatchdays] = useState<Matchday[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editData, setEditData] = useState<Partial<Matchday>>({});
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newMatchdayData, setNewMatchdayData] = useState<Partial<Matchday>>({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [matchdaysData, seasonsData, competitionsData] = await Promise.all([
        sportsApi.getMatchdays(),
        sportsApi.getSeasons(),
        sportsApi.getCompetitions(),
      ]);
      setMatchdays(matchdaysData);
      setSeasons(seasonsData);
      setCompetitions(competitionsData);
      setError(null);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (matchday: Matchday) => {
    setEditingId(matchday.id);
    setEditData({ ...matchday });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleSave = async (id: number) => {
    try {
      await sportsApi.updateMatchday(id, {
        name: editData.name,
        number: editData.number,
        startDate: editData.startDate,
        endDate: editData.endDate,
        isActive: editData.isActive,
        isCompleted: editData.isCompleted,
        seasonId: editData.seasonId,
        competitionId: editData.competitionId,
      });
      await loadData();
      setEditingId(null);
      setEditData({});
      setError(null);
    } catch (err) {
      console.error('Error updating matchday:', err);
      setError('Fehler beim Aktualisieren');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await sportsApi.deleteMatchday(id);
      await loadData();
      setDeleteConfirm(null);
      setError(null);
    } catch (err) {
      console.error('Error deleting matchday:', err);
      setError('Fehler beim Löschen');
    }
  };

  const handleStartCreate = () => {
    setIsCreating(true);
    setNewMatchdayData({
      name: 'Spieltag',
      number: 1,
      seasonId: 0,
      competitionId: 0,
      isActive: true,
      isCompleted: false,
    });
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewMatchdayData({});
  };

  const handleSaveNew = async () => {
    try {
      // Validierung
      if (!newMatchdayData.name || !newMatchdayData.name.trim()) {
        setError('Bitte geben Sie einen Namen ein');
        return;
      }
      if (!newMatchdayData.number || newMatchdayData.number < 1) {
        setError('Bitte geben Sie eine gültige Nummer ein');
        return;
      }
      if (!newMatchdayData.seasonId || newMatchdayData.seasonId === 0) {
        setError('Bitte wählen Sie eine Saison aus');
        return;
      }

      await sportsApi.createMatchday({
        name: newMatchdayData.name,
        number: newMatchdayData.number,
        startDate: newMatchdayData.startDate,
        endDate: newMatchdayData.endDate,
        isActive: newMatchdayData.isActive ?? true,
        isCompleted: newMatchdayData.isCompleted ?? false,
        seasonId: newMatchdayData.seasonId,
        competitionId: newMatchdayData.competitionId,
      });

      await loadData();
      setIsCreating(false);
      setNewMatchdayData({});
      setError(null);
    } catch (err) {
      console.error('Error creating matchday:', err);
      setError('Fehler beim Erstellen des Spieltags');
    }
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return '';
    return new Date(date).toISOString().split('T')[0];
  };

  const parseDate = (dateString: string) => {
    if (!dateString) return undefined;
    return new Date(dateString).toISOString();
  };

  if (loading) {
    return <div style={loadingStyle}>Laden...</div>;
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h2 style={titleStyle}><Calendar size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Spieltage Datenmanagement</h2>
        <button onClick={handleStartCreate} style={createButtonStyle} disabled={isCreating}>
          <Plus size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Neuer Spieltag
        </button>
      </div>

      {error && (
        <div style={errorBannerStyle}>
          {error}
          <button onClick={() => setError(null)} style={errorCloseButtonStyle}><X size={14} /></button>
        </div>
      )}

      <div style={tableContainerStyle}>
        <table style={tableStyle}>
          <thead>
            <tr style={headerRowStyle}>
              <th style={headerCellStyle}>ID</th>
              <th style={headerCellStyle}>Name</th>
              <th style={headerCellStyle}>Nummer</th>
              <th style={headerCellStyle}>Saison</th>
              <th style={headerCellStyle}>Wettbewerb</th>
              <th style={headerCellStyle}>Start</th>
              <th style={headerCellStyle}>Ende</th>
              <th style={headerCellStyle}>Aktiv</th>
              <th style={headerCellStyle}>Abgeschlossen</th>
              <th style={headerCellStyle}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {/* Neue Zeile für das Erstellen */}
            {isCreating && (
              <tr style={{...rowStyle, backgroundColor: '#f0f9ff'}}>
                <td style={cellStyle}>
                  <span style={newBadgeStyle}>NEU</span>
                </td>

                {/* Name */}
                <td style={cellStyle}>
                  <input
                    type="text"
                    value={newMatchdayData.name || ''}
                    onChange={(e) => setNewMatchdayData({ ...newMatchdayData, name: e.target.value })}
                    style={inputStyle}
                    placeholder="Name..."
                  />
                </td>

                {/* Nummer */}
                <td style={cellStyle}>
                  <input
                    type="number"
                    min="1"
                    value={newMatchdayData.number || ''}
                    onChange={(e) => setNewMatchdayData({ ...newMatchdayData, number: Number(e.target.value) })}
                    style={inputStyle}
                    placeholder="Nr."
                  />
                </td>

                {/* Saison */}
                <td style={cellStyle}>
                  <select
                    value={newMatchdayData.seasonId || 0}
                    onChange={(e) => setNewMatchdayData({ ...newMatchdayData, seasonId: Number(e.target.value) })}
                    style={selectStyle}
                  >
                    <option value={0}>Auswählen...</option>
                    {seasons.map(season => (
                      <option key={season.id} value={season.id}>
                        {season.name}
                      </option>
                    ))}
                  </select>
                </td>

                {/* Wettbewerb */}
                <td style={cellStyle}>
                  <select
                    value={newMatchdayData.competitionId || 0}
                    onChange={(e) => setNewMatchdayData({ ...newMatchdayData, competitionId: e.target.value ? Number(e.target.value) : undefined })}
                    style={selectStyle}
                  >
                    <option value={0}>Keine</option>
                    {competitions.map(comp => (
                      <option key={comp.id} value={comp.id}>
                        {comp.name}
                      </option>
                    ))}
                  </select>
                </td>

                {/* Start */}
                <td style={cellStyle}>
                  <input
                    type="date"
                    value={formatDate(newMatchdayData.startDate)}
                    onChange={(e) => setNewMatchdayData({ ...newMatchdayData, startDate: e.target.value ? parseDate(e.target.value) : undefined })}
                    style={inputStyle}
                  />
                </td>

                {/* Ende */}
                <td style={cellStyle}>
                  <input
                    type="date"
                    value={formatDate(newMatchdayData.endDate)}
                    onChange={(e) => setNewMatchdayData({ ...newMatchdayData, endDate: e.target.value ? parseDate(e.target.value) : undefined })}
                    style={inputStyle}
                  />
                </td>

                {/* Aktiv */}
                <td style={cellStyle}>
                  <input
                    type="checkbox"
                    checked={newMatchdayData.isActive ?? true}
                    onChange={(e) => setNewMatchdayData({ ...newMatchdayData, isActive: e.target.checked })}
                    style={checkboxStyle}
                  />
                </td>

                {/* Abgeschlossen */}
                <td style={cellStyle}>
                  <input
                    type="checkbox"
                    checked={newMatchdayData.isCompleted ?? false}
                    onChange={(e) => setNewMatchdayData({ ...newMatchdayData, isCompleted: e.target.checked })}
                    style={checkboxStyle}
                  />
                </td>

                {/* Aktionen */}
                <td style={cellStyle}>
                  <div style={actionsStyle}>
                    <button onClick={handleSaveNew} style={saveButtonStyle} title="Speichern">
                      <Check size={14} />
                    </button>
                    <button onClick={handleCancelCreate} style={cancelButtonStyle} title="Abbrechen">
                      <X size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            )}

            {/* Bestehende Spieltage */}
            {matchdays.map((matchday) => (
              <tr key={matchday.id} style={rowStyle}>
                {editingId === matchday.id ? (
                  <>
                    {/* ID (nicht editierbar) */}
                    <td style={cellStyle}>{matchday.id}</td>

                    {/* Name */}
                    <td style={cellStyle}>
                      <input
                        type="text"
                        value={editData.name || ''}
                        onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                        style={inputStyle}
                      />
                    </td>

                    {/* Nummer */}
                    <td style={cellStyle}>
                      <input
                        type="number"
                        min="1"
                        value={editData.number || ''}
                        onChange={(e) => setEditData({ ...editData, number: Number(e.target.value) })}
                        style={inputStyle}
                      />
                    </td>

                    {/* Saison */}
                    <td style={cellStyle}>
                      <select
                        value={editData.seasonId || 0}
                        onChange={(e) => setEditData({ ...editData, seasonId: Number(e.target.value) })}
                        style={selectStyle}
                      >
                        <option value={0}>Auswählen...</option>
                        {seasons.map(season => (
                          <option key={season.id} value={season.id}>
                            {season.name}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Wettbewerb */}
                    <td style={cellStyle}>
                      <select
                        value={editData.competitionId || 0}
                        onChange={(e) => setEditData({ ...editData, competitionId: e.target.value ? Number(e.target.value) : undefined })}
                        style={selectStyle}
                      >
                        <option value={0}>Keine</option>
                        {competitions.map(comp => (
                          <option key={comp.id} value={comp.id}>
                            {comp.name}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Start */}
                    <td style={cellStyle}>
                      <input
                        type="date"
                        value={formatDate(editData.startDate)}
                        onChange={(e) => setEditData({ ...editData, startDate: e.target.value ? parseDate(e.target.value) : undefined })}
                        style={inputStyle}
                      />
                    </td>

                    {/* Ende */}
                    <td style={cellStyle}>
                      <input
                        type="date"
                        value={formatDate(editData.endDate)}
                        onChange={(e) => setEditData({ ...editData, endDate: e.target.value ? parseDate(e.target.value) : undefined })}
                        style={inputStyle}
                      />
                    </td>

                    {/* Aktiv */}
                    <td style={cellStyle}>
                      <input
                        type="checkbox"
                        checked={editData.isActive ?? true}
                        onChange={(e) => setEditData({ ...editData, isActive: e.target.checked })}
                        style={checkboxStyle}
                      />
                    </td>

                    {/* Abgeschlossen */}
                    <td style={cellStyle}>
                      <input
                        type="checkbox"
                        checked={editData.isCompleted ?? false}
                        onChange={(e) => setEditData({ ...editData, isCompleted: e.target.checked })}
                        style={checkboxStyle}
                      />
                    </td>

                    {/* Aktionen */}
                    <td style={cellStyle}>
                      <div style={actionsStyle}>
                        <button onClick={() => handleSave(matchday.id)} style={saveButtonStyle} title="Speichern">
                          <Check size={14} />
                        </button>
                        <button onClick={handleCancelEdit} style={cancelButtonStyle} title="Abbrechen">
                          <X size={14} />
                        </button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    {/* Ansichtsmodus */}
                    <td style={cellStyle}>{matchday.id}</td>
                    <td style={cellStyle}>{matchday.name}</td>
                    <td style={cellStyle}>{matchday.number}</td>
                    <td style={cellStyle}>
                      {matchday.season?.name || `ID: ${matchday.seasonId}`}
                    </td>
                    <td style={cellStyle}>
                      {matchday.competition?.name || '-'}
                    </td>
                    <td style={cellStyle}>
                      {matchday.startDate ? new Date(matchday.startDate).toLocaleDateString('de-DE') : '-'}
                    </td>
                    <td style={cellStyle}>
                      {matchday.endDate ? new Date(matchday.endDate).toLocaleDateString('de-DE') : '-'}
                    </td>
                    <td style={cellStyle}>
                      <span style={matchday.isActive ? activeBadgeStyle : inactiveBadgeStyle}>
                        {matchday.isActive ? <Check size={14} /> : <X size={14} />}
                      </span>
                    </td>
                    <td style={cellStyle}>
                      <span style={matchday.isCompleted ? completedBadgeStyle : pendingBadgeStyle}>
                        {matchday.isCompleted ? <Check size={14} /> : <X size={14} />}
                      </span>
                    </td>
                    <td style={cellStyle}>
                      <div style={actionsStyle}>
                        <button onClick={() => handleEdit(matchday)} style={editButtonStyle} title="Bearbeiten">
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => setDeleteConfirm(matchday.id)} style={deleteButtonStyle} title="Löschen">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div style={modalOverlayStyle} onClick={() => setDeleteConfirm(null)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h3 style={modalTitleStyle}>Spieltag löschen?</h3>
            <p style={modalTextStyle}>
              Möchten Sie diesen Spieltag wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div style={modalActionsStyle}>
              <button onClick={() => setDeleteConfirm(null)} style={modalCancelButtonStyle}>
                Abbrechen
              </button>
              <button onClick={() => handleDelete(deleteConfirm)} style={modalDeleteButtonStyle}>
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles
const containerStyle: React.CSSProperties = {
  padding: '1rem',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1rem',
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '1.5rem',
  color: '#2c3e50',
};

const createButtonStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  background: '#10b981',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontWeight: '500',
};

const loadingStyle: React.CSSProperties = {
  padding: '2rem',
  textAlign: 'center',
  color: '#6c757d',
};

const tableContainerStyle: React.CSSProperties = {
  overflowX: 'auto',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.875rem',
};

const headerRowStyle: React.CSSProperties = {
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #dee2e6',
};

const headerCellStyle: React.CSSProperties = {
  padding: '0.75rem',
  textAlign: 'left',
  fontWeight: '600',
  color: '#495057',
  whiteSpace: 'nowrap',
};

const rowStyle: React.CSSProperties = {
  borderBottom: '1px solid #e5e7eb',
};

const cellStyle: React.CSSProperties = {
  padding: '0.75rem',
  verticalAlign: 'middle',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.375rem',
  border: '1px solid #ced4da',
  borderRadius: '4px',
  fontSize: '0.875rem',
};

const selectStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.375rem',
  border: '1px solid #ced4da',
  borderRadius: '4px',
  fontSize: '0.875rem',
  backgroundColor: 'white',
};

const checkboxStyle: React.CSSProperties = {
  width: '1.25rem',
  height: '1.25rem',
  cursor: 'pointer',
};

const actionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: '0.5rem',
};

const editButtonStyle: React.CSSProperties = {
  padding: '0.25rem 0.5rem',
  background: '#3b82f6',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '1rem',
};

const deleteButtonStyle: React.CSSProperties = {
  padding: '0.25rem 0.5rem',
  background: '#dc3545',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '1rem',
};

const saveButtonStyle: React.CSSProperties = {
  padding: '0.25rem 0.5rem',
  background: '#10b981',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '1rem',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '0.25rem 0.5rem',
  background: '#6c757d',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '1rem',
};

const notesCellStyle: React.CSSProperties = {
  maxWidth: '200px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const activeBadgeStyle: React.CSSProperties = {
  padding: '0.25rem 0.5rem',
  borderRadius: '12px',
  fontSize: '0.75rem',
  background: '#d1fae5',
  color: '#065f46',
  fontWeight: '500',
};

const inactiveBadgeStyle: React.CSSProperties = {
  padding: '0.25rem 0.5rem',
  borderRadius: '12px',
  fontSize: '0.75rem',
  background: '#fee2e2',
  color: '#991b1b',
  fontWeight: '500',
};

const completedBadgeStyle: React.CSSProperties = {
  padding: '0.25rem 0.5rem',
  borderRadius: '12px',
  fontSize: '0.75rem',
  background: '#dbeafe',
  color: '#1e40af',
  fontWeight: '500',
};

const pendingBadgeStyle: React.CSSProperties = {
  padding: '0.25rem 0.5rem',
  borderRadius: '12px',
  fontSize: '0.75rem',
  background: '#fef3c7',
  color: '#92400e',
  fontWeight: '500',
};

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '8px',
  padding: '1.5rem',
  maxWidth: '400px',
  width: '90%',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
};

const modalTitleStyle: React.CSSProperties = {
  margin: '0 0 1rem 0',
  fontSize: '1.25rem',
  color: '#2c3e50',
};

const modalTextStyle: React.CSSProperties = {
  margin: '0 0 1.5rem 0',
  color: '#6c757d',
};

const modalActionsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '0.5rem',
};

const modalCancelButtonStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  border: '1px solid #ced4da',
  borderRadius: '4px',
  background: 'white',
  color: '#495057',
  cursor: 'pointer',
};

const modalDeleteButtonStyle: React.CSSProperties = {
  padding: '0.5rem 1rem',
  border: 'none',
  borderRadius: '4px',
  background: '#dc3545',
  color: 'white',
  cursor: 'pointer',
};

const newBadgeStyle: React.CSSProperties = {
  padding: '0.25rem 0.5rem',
  borderRadius: '12px',
  fontSize: '0.7rem',
  background: '#10b981',
  color: 'white',
  fontWeight: 'bold',
};

const errorBannerStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  background: '#fee2e2',
  border: '1px solid #fecaca',
  borderRadius: '6px',
  color: '#dc2626',
  marginBottom: '1rem',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const errorCloseButtonStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: '#dc2626',
  cursor: 'pointer',
  fontSize: '1.2rem',
  padding: '0 0.5rem',
};

export default MatchdaysDataTable;

